// Import the proper types from the search module
import type { SearchCriteriaElements } from '../slice/types';
import type { 
  RecentSearchApiResponse, 
  TypeAheadDropdownItem, // Changed from TypeAheadSuggestions
} from '../interfaces';

type TranslationDict = Record<string, Record<string, string>>

// ✅ NEW: Type for individual offer
export interface OfferItem {
  offerId: string;
  offerName: string;
  offerDesc: string | null;
  navURL: string;
  percentOff: number;
  rewardEndDate: string | null;
  realTimeInd: string | null;
}

// ✅ NEW: Type for offers response
export interface OffersResponse {
  offersList: OfferItem[];
}

export interface SearchLocalStorageData {
  recentSearchData?: RecentSearchApiResponse;
  selectedSuggestion?: TypeAheadDropdownItem; // Changed from TypeAheadSuggestions
  selectedTypeAheadItemRefID?: string;
  utilityTranslation?: TranslationDict;
  localStoragesearchCriteriaElements?: SearchCriteriaElements;
  loadSearchCriteriaElements?: SearchCriteriaElements;
  offerDetails?: OffersResponse; // ✅ Added the offers type
  isLengthOfStaySelected?: string;
}

export class SearchStorageManager {
  private static readonly STORAGE_KEY = 'search_LocalStorageData';

  /**
   * Get the entire search localStorage data
   */
  static getSearchData(): SearchLocalStorageData {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Error parsing search localStorage data:', error);
      return {};
    }
  }

  /**
   * Set the entire search localStorage data
   */
  static setSearchData(data: SearchLocalStorageData): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving search localStorage data:', error);
    }
  }

  /**
   * Get a specific search data property
   */
  static getItem<K extends keyof SearchLocalStorageData>(key: K): SearchLocalStorageData[K] {
    const data = this.getSearchData();
    return data[key];
  }

  /**
   * Set a specific search data property
   */
  static setItem<K extends keyof SearchLocalStorageData>(key: K, value: SearchLocalStorageData[K]): void {
    const data = this.getSearchData();
    data[key] = value;
    this.setSearchData(data);
  }

  /**
   * Remove a specific search data property
   */
  static removeItem<K extends keyof SearchLocalStorageData>(key: K): void {
    const data = this.getSearchData();
    delete data[key];
    this.setSearchData(data);
  }

  /**
   * Clear all search data
   */
  static clear(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // ✅ NEW: Utility methods for offer details
  /**
   * Get offer details from localStorage
   */
  static getOfferDetails(): OffersResponse | undefined {
    return this.getItem('offerDetails');
  }

  /**
   * Set offer details in localStorage
   */
  static setOfferDetails(offerDetails: OffersResponse): void {
    this.setItem('offerDetails', offerDetails);
  }

  /**
   * Get a specific offer by ID
   */
  static getOfferById(offerId: string): OfferItem | undefined {
    const offerDetails = this.getOfferDetails();
    return offerDetails?.offersList.find(offer => offer.offerId === offerId);
  }

  
}